import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import waitForMediaCompletion from "./waitForMediaCompletion";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { downloadBlobFile } from "@/utils/helper/downloadBlob";
import { generateParams } from "@/utils/helper/generateParams";
import { swalErrorDefault } from "@/utils/lib/swal";

type HistoryUpdateHomepassType = {
  rfsId?: string;
};

interface HistoryProfileHomepassData {
  id: string;
  clusterId: string;
  status: string;
  totalBizzHomepass: number | null;
  totalHomepassAdditional: number | null;
  totalHomepassInvalid: number | null;
  totalHpApd: string | number | null;
  totalHpRfs: number | null;
  totalNeedSurvey: number | null;
  totalNoNeedSurvey: number | null;
  totalResidenceHomepass: number | null;
  updatedBy: string;
  updatedByName: string;
  updatedDate: string | null;
  fileAbdKmz: string | null;
  fileHptar: string | null;
  fileCableSlingwireQspan: string | null;
  vendor: {
    id: string;
    company: string;
    companyCode: string;
  };
}

async function getGenerateHistoryUpdateHomepass(
  queryFunctionContext: QueryFunctionContext,
): Promise<HistoryProfileHomepassData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, generateHistoryUpdateHomepassQueryKey] = queryKey;
  const { rfsId } =
    generateHistoryUpdateHomepassQueryKey as HistoryUpdateHomepassType;

  const response = await axiosInstance.get(
    `/api/crud/historyUpdateHomepass?rfs.id=${
      rfsId ?? ""
    }&sort=createdDate&asc=false`,
  );
  return response.data.data.content;
}
function useHistoryUpdateHomepass(
  generateHistoryUpdateHomepassQueryKey: HistoryUpdateHomepassType = {},
  options?: {
    enabled?: boolean;
    staleTime?: number;
  },
) {
  const { rfsId } = generateHistoryUpdateHomepassQueryKey;
  return useQuery({
    ...options,
    queryKey: ["historyUpdateHomepass", { rfsId }],
    queryFn: getGenerateHistoryUpdateHomepass,
  });
}

/*
 * Export List history homepass to Excel
 */
async function exportHistoryHomepass(config: DataConfig = {}): Promise<void> {
  const params = {
    ...config,
    entityId: config.entityId ?? undefined,
  };
  const createResponse = await axiosInstance.get(
    `/api/crud/historyUpdateHomepass/excel${generateParams(params)}`,
  );

  const result = await waitForMediaCompletion(
    `/api/crud/media/${createResponse.data.data.id}`,
  );

  if (result.success) {
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: {
          path: result.path,
          fileName: result.originalFileName,
          entityId: config.entityId,
        },
        responseType: "blob",
      },
    );

    downloadBlobFile(
      new Blob([downloadResponse.data]),
      result.originalFileName,
    );
  } else {
    swalErrorDefault.fire({
      text: result.error,
    });
  }
}

function useExportHistoryHomepass() {
  return useMutation({
    mutationFn: exportHistoryHomepass,
  });
}

/*
 * Export List fooltList
 */
async function downloadAllZipHistoryHomepass(
  config: DataConfig = {},
): Promise<void> {
  const params = {
    ...config,
    entityId: config.entityId ?? undefined,
  };
  const createResponse = await axiosInstance.get(
    `/api/crud/historyUpdateHomepass/zip${generateParams(params)}`,
  );

  const result = await waitForMediaCompletion(
    `/api/crud/media/${createResponse.data.data.id}`,
  );

  if (result.success) {
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: {
          path: result.path,
          fileName: result.originalFileName,
          entityId: config.entityId,
        },
        responseType: "blob",
      },
    );

    downloadBlobFile(
      new Blob([downloadResponse.data]),
      result.originalFileName,
    );
  } else {
    swalErrorDefault.fire({
      text: result.error,
    });
    throw new Error(result.error);
  }
}

function useDownloadAllZipHistoryHomepass() {
  return useMutation({
    mutationFn: downloadAllZipHistoryHomepass,
  });
}

export {
  useHistoryUpdateHomepass,
  useExportHistoryHomepass,
  useDownloadAllZipHistoryHomepass,
};
